import React, { useMemo } from 'react';
import { Sequence } from 'remotion';
import { computeGroupBBox } from '../canvas/utils/group-units';
import type { EditorStarterItem } from '../items/item-type';
import { getRectAfterCrop } from '../utils/get-dimensions-after-crop';
import { useAllItems } from '../utils/use-context';
import { useCanvasTransformationScale } from '../utils/canvas-transformation-context';

/**
 * 그룹을 한 덩어리로 선택했을 때, 멤버들의 union bbox 에 단일 outline 을 그린다.
 * - 개별 멤버 outline 은 SelectionOutline 에서 숨긴다(isGroupBlockSelected).
 * - 클릭/더블클릭 hit-area 는 멤버별 SelectionOutline 이 그대로 담당하므로, 이 outline 은
 *   pointerEvents:none 으로 시각 표시만 한다.
 */
export const GroupSelectionOutline: React.FC<{ memberIds: string[] }> = ({
  memberIds,
}) => {
  const { items } = useAllItems();
  const scale = useCanvasTransformationScale();

  const members = useMemo(
    () =>
      memberIds
        .map((id) => items[id])
        .filter((item): item is EditorStarterItem => Boolean(item)),
    [memberIds, items],
  );

  // bbox 는 멤버의 visibleBox(getRectAfterCrop, crop 반영) union 으로 계산해
  // 개별 SelectionOutline outline 좌표와 어긋나지 않게 한다.
  const geometry = useMemo(() => {
    if (members.length === 0) return null;
    const bbox = computeGroupBBox(
      members.map((m) => {
        const rect = getRectAfterCrop(m);
        return {
          left: rect.left,
          top: rect.top,
          width: rect.width,
          height: rect.height,
        };
      }),
    );
    const from = Math.min(...members.map((m) => m.from));
    const duration =
      Math.max(...members.map((m) => m.from + m.durationInFrames)) - from;
    return { bbox, from, duration };
  }, [members]);

  if (geometry === null) return null;

  const { bbox, from, duration } = geometry;
  const border = Math.ceil(2 / scale);

  return (
    <Sequence
      from={from}
      durationInFrames={Math.max(1, duration)}
      layout="none"
    >
      <div
        style={{
          position: 'absolute',
          left: bbox.left,
          top: bbox.top,
          width: bbox.width,
          height: bbox.height,
          outline: `${border}px solid var(--color-editor-starter-accent)`,
          pointerEvents: 'none',
          zIndex: 1000,
        }}
      />
    </Sequence>
  );
};
